import Util from "../../Util";
import React, { useState } from "react";
import { DatePicker, Space, message, Table, Button, Typography } from "antd";

const { RangePicker } = DatePicker;
const moment = require("moment-timezone");

const TIMEZONE = "Asia/Bangkok";
const { Title } = Typography;

function OrderList() {
  const [messageApi, contextHolder] = message.useMessage();
  const [dataList, setDataList] = useState([]);
  const [summaryDataList, setSummaryDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadMessage, setLoadMessage] = useState("");
  const [dataMessage, setDataMessage] = useState("");

  const downloadDataWithDateString = async (dateStringFrom, dateStringTo) => {
    const momentFrom = moment.tz(dateStringFrom, TIMEZONE).valueOf();
    const momentTo = moment.tz(dateStringTo, TIMEZONE).valueOf();

    const data = await Util.sendGet("/api/moresheetStaff/getOrderReport", {
      epochSecFrom: Math.ceil(momentFrom / 1000),
      epochSecTo: Math.ceil(momentTo / 1000),
    });

    if (!data.success) {
      messageApi.error("Error Loading");
      return;
    }
    const formatted = data.data.map((row) => {
      const paidTime = moment(row.created).tz(TIMEZONE);
      const price = row.price === null ? 0 : row.price;
      return {
        periodYear: paidTime.format("YYYY"),
        periodMonth: paidTime.format("YYYY-MM"),
        periodDay: paidTime.format("YYYY-MM-DD"),
        time: paidTime.format("HH:mm:ss"),
        orderCode: row._id,
        totalSales: price.toFixed(2),
        costOfSales: (price * 0.6).toFixed(2),
      };
    });
    return formatted;
  };

  const processSummaryData = (data) => {
    if (!data || data.length === 0) {
      return [];
    }

    const summary = {};

    for (const row of data) {
      const day = row.periodDay;
      if (!summary.hasOwnProperty(day)) {
        summary[day] = {
          date: day,
          orderFirstId: row.orderCode,
          orderLastId: row.orderCode,
          totalSales: 0,
          totalCostOfSales: 0,
        };
      }
      summary[day].orderLastId = row.orderCode;
      summary[day].totalSales += parseFloat(row.totalSales);
      summary[day].totalCostOfSales += parseFloat(row.costOfSales);
    }

    return Object.values(summary)
      .map((dayData) => ({
        ...dayData,
        orderNumbers: `${dayData.orderFirstId} - ${dayData.orderLastId}`,
        totalSales: dayData.totalSales.toFixed(2),
        totalCostOfSales: dayData.totalCostOfSales.toFixed(2),
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  };

  const onChangeDate = async (_, dateString) => {
    setLoading(true);
    const dateStringFrom = `${dateString[0]} 00:00:00`;
    const dateStringTo = `${dateString[1]} 23:59:59`;
    setLoadMessage(`กำลังโหลดข้อมูล ${dateStringFrom} ถึง ${dateStringTo}`);
    const data = await downloadDataWithDateString(dateStringFrom, dateStringTo);
    setDataList(data);
    setSummaryDataList(processSummaryData(data));
    setDataMessage(`ข้อมูล ${dateStringFrom} ถึง ${dateStringTo}`);
    setLoading(false);
  };

  const onChangeMonth = async (_, dateString) => {
    setLoading(true);
    let finalData = [];

    const toYearMonth = (str) => {
      const splitted = str.split("-");
      return { year: parseInt(splitted[0]), month: parseInt(splitted[1]) };
    };
    const downloadMonth = async (year, month) => {
      setLoadMessage(`กำลังโหลดข้อมูล ${year} - ${month}`);
      const lastday = moment(
        `${year}-${String(month).padStart(2, "0")}`,
        "YYYY-MM"
      ).daysInMonth();

      const dateStringFrom = `${year}-${String(month).padStart(
        2,
        "0"
      )}-01 00:00:00`;
      const dateStringTo = `${year}-${String(month).padStart(2, "0")}-${String(
        lastday
      ).padStart(2, "0")} 23:59:59`;

      const data = await downloadDataWithDateString(
        dateStringFrom,
        dateStringTo
      );
      finalData = [...finalData, ...data];
    };
    const startMonth = toYearMonth(dateString[0]);
    const endMonth = toYearMonth(dateString[1]);
    if (startMonth.year !== endMonth.year) {
      // Start year
      for (let month = startMonth.month; month <= 12; month++) {
        const year = startMonth.year;
        await downloadMonth(year, month);
      }
      // Middle years
      for (let year = startMonth.year + 1; year <= endMonth.year - 1; year++) {
        for (let month = 1; month <= 12; month++) {
          await downloadMonth(year, month);
        }
      }
      // end years
      for (let month = 1; month <= endMonth.month; month++) {
        const year = endMonth.year;
        await downloadMonth(year, month);
      }
    } else {
      for (let month = startMonth.month; month <= endMonth.month; month++) {
        const year = startMonth.year;
        await downloadMonth(year, month);
      }
    }
    setDataList(finalData);
    setSummaryDataList(processSummaryData(finalData));
    setDataMessage(
      `ข้อมูล ${startMonth.year}-${startMonth.month} ถึง ${endMonth.year}-${endMonth.month}`
    );
    setLoading(false);
  };

  const columns = [
    {
      title: "Period Year",
      dataIndex: "periodYear",
      key: "periodYear",
    },
    {
      title: "Period Month",
      dataIndex: "periodMonth",
      key: "periodMonth",
    },
    {
      title: "Period Day",
      dataIndex: "periodDay",
      key: "periodDay",
    },
    {
      title: "Time",
      dataIndex: "time",
      key: "time",
    },
    {
      title: "Order Code",
      dataIndex: "orderCode",
      key: "orderCode",
    },
    {
      title: "Total Sales",
      dataIndex: "totalSales",
      key: "orderCode",
    },
    {
      title: "Cost of Sales",
      dataIndex: "costOfSales",
      key: "costOfSales",
    },
  ];

  const summaryColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Order numbers",
      dataIndex: "orderNumbers",
      key: "orderNumbers",
    },
    {
      title: "Total Sales",
      dataIndex: "totalSales",
      key: "totalSales",
    },
    {
      title: "Total cost of sales",
      dataIndex: "totalCostOfSales",
      key: "totalCostOfSales",
    },
  ];

  const downloadAsCsv = (dataToDownload, columnsToDownload, fileName) => {
    if (!dataToDownload || dataToDownload.length === 0) {
      messageApi.warning("No data to download.");
      return;
    }

    const data = [];
    data.push(columnsToDownload.map((col) => col.title).join(","));
    for (const x of dataToDownload) {
      data.push(columnsToDownload.map((col) => x[col.dataIndex]).join(","));
    }

    const text = data.join("\n");
    const blob = new Blob([text], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Space direction="vertical" size={12}>
      {contextHolder}
      {loading ? (
        <div>{loadMessage}</div>
      ) : (
        <React.Fragment>
          <div>
            เลือกช่วงวัน / ช่วงเดือน อย่างใดอย่างหนึ่ง
            ระบบจะดึงข้อมูลทันทีหลังจากเลือกเสร็จ
            โปรดเลือกช่วงเดือนถ้าเป็นไปได้เพราะการเลือกช่วงวันกว้างๆ(มากกว่า90วัน)อาจจะมีปัญหาได้
          </div>
          <RangePicker onChange={onChangeDate} />
          <RangePicker picker="month" onChange={onChangeMonth} />
          {dataList.length > 0 && (
            <React.Fragment>
              <div>{dataMessage}</div>
              <Button
                type="primary"
                size="large"
                onClick={() => downloadAsCsv(dataList, columns, "data.csv")}
              >
                Download as CSV
              </Button>
            </React.Fragment>
          )}
          <Table dataSource={dataList} columns={columns} />

          {summaryDataList.length > 0 && (
            <React.Fragment>
              <Title level={4}>Daily Summary</Title>
              <Button
                type="primary"
                size="large"
                onClick={() =>
                  downloadAsCsv(
                    summaryDataList,
                    summaryColumns,
                    "summary_data.csv"
                  )
                }
              >
                Download Summary as CSV
              </Button>
              <Table
                dataSource={summaryDataList}
                columns={summaryColumns}
                rowKey="date"
              />
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </Space>
  );
}

export default OrderList;
